import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Assignment as ConsignmentIcon,
  Receipt as SaleContractIcon,
  Description as DocIcon,
} from '@mui/icons-material';
import vehicleService from '../../services/vehicleService';
import saleService from '../../services/saleService';
import { Vehicle } from '../../types';
import { Sale } from '../../types';

const Contracts: React.FC = () => {
  const navigate = useNavigate();
  const [veiculosConsignados, setVeiculosConsignados] = useState<Vehicle[]>([]);
  const [vendas, setVendas] = useState<Sale[]>([]);
  const [loadingVeiculos, setLoadingVeiculos] = useState(false);
  const [loadingVendas, setLoadingVendas] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [selectedSaleId, setSelectedSaleId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadVeiculos = async () => {
      setLoadingVeiculos(true);
      try {
        const list = await vehicleService.getAllVehicles();
        const consignados = (list || []).filter((v) => v.is_consignado);
        setVeiculosConsignados(consignados);
        if (consignados.length && !selectedVehicleId) {
          setSelectedVehicleId(String(consignados[0].id));
        }
      } catch (e) {
        console.error(e);
        setError('Não foi possível carregar os veículos consignados.');
      } finally {
        setLoadingVeiculos(false);
      }
    };
    loadVeiculos();
  }, []);

  useEffect(() => {
    const loadVendas = async () => {
      setLoadingVendas(true);
      try {
        const list = await saleService.getAllSales();
        setVendas(list || []);
        if (list?.length && !selectedSaleId) {
          setSelectedSaleId(String(list[0].id));
        }
      } catch (e) {
        console.error(e);
        setError('Não foi possível carregar as vendas.');
      } finally {
        setLoadingVendas(false);
      }
    };
    loadVendas();
  }, []);

  const handleGerarConsignacao = () => {
    if (selectedVehicleId) navigate(`/contratos/consignacao/${selectedVehicleId}`);
  };

  const handleGerarVenda = () => {
    if (selectedSaleId) navigate(`/contratos/venda/${selectedSaleId}`);
  };

  const getVehicleLabel = (v: Vehicle) =>
    `${v.brand} ${v.model} ${v.year} - ${v.plate}${v.consignment_owner_name ? ` (${v.consignment_owner_name})` : ''}`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
        Contratos
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Gere contratos de consignação e de compra e venda. Os documentos devem ser revisados e ajustados pelo setor
        jurídico antes do uso.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Contrato de Consignação */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ConsignmentIcon color="primary" />
                <Typography variant="h6">Contrato de Consignação</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Para veículos que o cliente deixa na loja para venda. Gera o contrato entre proprietário (consignante) e
                loja (consignatário).
              </Typography>
              {loadingVeiculos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : veiculosConsignados.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum veículo consignado cadastrado. Cadastre um veículo e marque como consignado para gerar o
                  contrato.
                </Typography>
              ) : (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Veículo consignado</InputLabel>
                    <Select
                      value={selectedVehicleId}
                      label="Veículo consignado"
                      onChange={(e) => setSelectedVehicleId(e.target.value)}
                    >
                      {veiculosConsignados.map((v) => (
                        <MenuItem key={v.id} value={String(v.id)}>
                          {getVehicleLabel(v)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<DocIcon />}
                    onClick={handleGerarConsignacao}
                    fullWidth
                    disabled={!selectedVehicleId}
                  >
                    Gerar contrato de consignação
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contrato de Venda */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SaleContractIcon color="primary" />
                <Typography variant="h6">Contrato de Compra e Venda</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Para vendas já realizadas. Gera o contrato entre loja (vendedor) e comprador com dados do veículo e da
                venda.
              </Typography>
              {loadingVendas ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : vendas.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma venda cadastrada. Registre uma venda para gerar o contrato.
                </Typography>
              ) : (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Venda</InputLabel>
                    <Select
                      value={selectedSaleId}
                      label="Venda"
                      onChange={(e) => setSelectedSaleId(e.target.value)}
                    >
                      {vendas.map((s) => (
                        <MenuItem key={s.id} value={String(s.id)}>
                          Venda #{s.id} – {s.vehicle?.brand} {s.vehicle?.model} {s.vehicle?.year} –{' '}
                          {s.buyer_name || s.customer_name || 'Comprador'} –{' '}
                          {s.sale_price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<DocIcon />}
                    onClick={handleGerarVenda}
                    fullWidth
                    disabled={!selectedSaleId}
                  >
                    Gerar contrato de venda
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Contracts;
